/**
 * 골든 이벤트 시스템
 * 5-10분마다 랜덤 골든 이벤트 출현
 * 업그레이드/버프 반영: golden1(지속+50%), golden2(빈도2배),
 * golden_master(지속2배), buff_golden(레벨당+2%), meta_golden_boost(빈도2배)
 */

import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { GOLDEN_EVENTS, GOLDEN_CONFIG, getGoldenIntervalMultiplier } from '../config/goldenEvents.js';

function getGoldenDisplayDurationMultiplier(state: GameState): number {
  let mult = 1;
  // up_global_golden1: 골든 지속시간 +50%
  if (state.purchasedUpgrades.includes('up_global_golden1')) mult *= 1.5;
  // up_res_golden_master: 지속시간 2배
  if (state.purchasedUpgrades.includes('up_res_golden_master')) mult *= 2;
  return mult;
}

function getNextSpawnTime(now: number, state: GameState): number {
  const intervalMult = getGoldenIntervalMultiplier(state);
  const min = GOLDEN_CONFIG.minInterval * intervalMult;
  const max = GOLDEN_CONFIG.maxInterval * intervalMult;
  return now + Math.random() * (max - min) + min;
}

export const goldenEventSystem: TickSystem<GameState, GameAction> = {
  name: 'goldenEvent',

  update(state) {
    if (!state.isRunning) return state;

    const now = Date.now();
    let newState = { ...state };

    // 골든 이벤트 만료 체크
    if (state.goldenEvent.active && now > state.goldenEvent.expiresAt) {
      newState.goldenEvent = {
        ...state.goldenEvent,
        active: false,
        type: null,
        spawnedAt: 0,
        expiresAt: 0,
        nextSpawnTime: getNextSpawnTime(now, state),
      };
      return newState;
    }

    // 새 골든 이벤트 출현 체크
    if (!state.goldenEvent.active && now >= state.goldenEvent.nextSpawnTime) {
      // 확률에 따라 골든 타입 선택
      let random = Math.random();
      let selectedEvent = GOLDEN_EVENTS[0];

      let cumulative = 0;
      for (const event of GOLDEN_EVENTS) {
        cumulative += event.probability;
        if (random <= cumulative) {
          selectedEvent = event;
          break;
        }
      }

      const displayDuration = GOLDEN_CONFIG.displayDuration * getGoldenDisplayDurationMultiplier(state);

      newState.goldenEvent = {
        active: true,
        type: selectedEvent.id,
        spawnedAt: now,
        expiresAt: now + displayDuration,
        nextSpawnTime: 0 // 클레임 후 재설정
      };
      return newState;
    }

    return state;
  }
};
